import type { Task, FullProject } from '../../types';
import {
  areProjectIdsMatching,
  filterAndSortRealProjects,
  isCommProject,
  isInboxProject,
  isSystemProject,
} from '../../utils/idNormalizer';
import { extractNormalizedPersonCandidates, isPersonRecord } from './personModel';

function isCommunicationItem(t: Task, inCommProject: boolean): boolean {
  if (t.type === 'milestone' || t.type === 'deadline') return false;
  if (inCommProject) return true;
  return (
    isPersonRecord(t) ||
    t.category === 'Kommunikation' ||
    t.type === 'cooperation' ||
    t.type === 'workshop' ||
    Boolean(t.contactId) ||
    Boolean(t.personKey) ||
    String(t.id).startsWith('sample_contact_') ||
    String(t.id).startsWith('person_sample_contact_')
  );
}

/**
 * Heals existing contacts and Vorgänge in `comm_internal` (and any leaked into `inbox_inforum`)
 * so that every contact and Vorgang belongs to at least one active real user project, guaranteeing
 * that the Global Communication Kanban is the exact cumulative union of all project-specific Kanbans.
 * Returns { updatedProjects, changedTasks, affectedProjectIds } if any tasks were healed.
 */
export function healCommProjectAssignments(allProjects: FullProject[]): {
  updatedProjects: FullProject[];
  changedTasks: Task[];
  affectedProjectIds: string[];
} {
  const realProjects = filterAndSortRealProjects(allProjects);
  if (realProjects.length === 0) {
    return { updatedProjects: allProjects, changedTasks: [], affectedProjectIds: [] };
  }

  const resolveValidRealPid = (pid?: string): string | null => {
    if (!pid || isSystemProject(pid)) return null;
    const matched = realProjects.find(rp => areProjectIdsMatching(rp.id, pid));
    return matched ? matched.id : null;
  };

  const projectByPersonName = new Map<string, Set<string>>();
  const projectByContactId = new Map<string, Set<string>>();

  const registerMatch = (map: Map<string, Set<string>>, key: string, projId: string) => {
    if (!key || !projId) return;
    let set = map.get(key);
    if (!set) {
      set = new Set();
      map.set(key, set);
    }
    set.add(projId);
  };

  realProjects.forEach(proj => {
    (proj.metadata?.stakeholders || []).forEach(s => {
      extractNormalizedPersonCandidates(s?.name).forEach(n => {
        registerMatch(projectByPersonName, n, proj.id);
      });
    });
    (proj.tasks || []).forEach(t => {
      if (t.archived) return;
      if (t.contactId) registerMatch(projectByContactId, String(t.contactId), proj.id);
      if (isPersonRecord(t)) {
        registerMatch(projectByContactId, String(t.id), proj.id);
        extractNormalizedPersonCandidates(t.name || t.assignee).forEach(n => {
          registerMatch(projectByPersonName, n, proj.id);
        });
      }
      if (t.assignee) {
        t.assignee.split(/[,;/]+/).forEach(a => {
          extractNormalizedPersonCandidates(a).forEach(n => {
            registerMatch(projectByPersonName, n, proj.id);
          });
        });
      }
    });
  });

  allProjects.forEach(proj => {
    if (!isSystemProject(proj.id, proj.name)) return;
    (proj.tasks || []).forEach(t => {
      if (!isPersonRecord(t)) return;
      const existingRealIds = [
        ...(t.projectId ? [resolveValidRealPid(t.projectId)] : []),
        ...(Array.isArray(t.projectIds) ? t.projectIds.map(pid => resolveValidRealPid(pid)) : []),
      ].filter((pid): pid is string => Boolean(pid));
      const candidates = extractNormalizedPersonCandidates(t.name || t.assignee);
      existingRealIds.forEach(pid => {
        registerMatch(projectByContactId, String(t.id), pid);
        candidates.forEach(n => registerMatch(projectByPersonName, n, pid));
      });
    });
  });

  const collectMatchedRealProjectIds = (t: Task, projWorkspace?: string): string[] => {
    const matchedPids = new Set<string>();
    const directPid = resolveValidRealPid(t.projectId);
    if (directPid) matchedPids.add(directPid);
    if (Array.isArray(t.projectIds)) {
      t.projectIds.forEach(pid => {
        const validPid = resolveValidRealPid(pid);
        if (validPid) matchedPids.add(validPid);
      });
    }
    if (t.contactId && projectByContactId.has(String(t.contactId))) {
      projectByContactId.get(String(t.contactId))!.forEach(pid => matchedPids.add(pid));
    }
    if (projectByContactId.has(String(t.id))) {
      projectByContactId.get(String(t.id))!.forEach(pid => matchedPids.add(pid));
    }
    const rawName = isPersonRecord(t) ? t.name || t.assignee : t.assignee || t.name;
    extractNormalizedPersonCandidates(rawName).forEach(norm => {
      if (projectByPersonName.has(norm)) {
        projectByPersonName.get(norm)!.forEach(pid => matchedPids.add(pid));
      }
    });

    if (matchedPids.size === 0) {
      const taskWs = t.workspace || projWorkspace || 'professional';
      const wsRealProjects = realProjects.filter(rp => (rp.workspace || 'professional') === taskWs);
      const fallbackProj = wsRealProjects[0] || realProjects[0];
      if (fallbackProj) {
        matchedPids.add(fallbackProj.id);
      }
    }

    return Array.from(matchedPids);
  };

  // Pre-pass: register healed project IDs for person records so linked Vorgänge inherit them
  allProjects.forEach(proj => {
    if (!isSystemProject(proj.id, proj.name)) return;
    (proj.tasks || []).forEach(t => {
      if (!isPersonRecord(t)) return;
      const realIdList = collectMatchedRealProjectIds(t, proj.workspace);
      const candidates = extractNormalizedPersonCandidates(t.name || t.assignee);
      realIdList.forEach(pid => {
        registerMatch(projectByContactId, String(t.id), pid);
        candidates.forEach(n => registerMatch(projectByPersonName, n, pid));
      });
    });
  });

  const changedTasks: Task[] = [];
  const affectedSet = new Set<string>();
  const tasksToMoveToProject = new Map<string, Task[]>();
  const personRecordsToMoveToComm: Task[] = [];

  const updatedProjects = allProjects
    .map(proj => {
      const isComm = isCommProject(proj.id);
      const isInbox = isInboxProject(proj.id) || isSystemProject(proj.id, proj.name);
      if (!isComm && !isInbox) {
        // Real project: clean any stale system project IDs (e.g. 'inbox_inforum') from task.projectIds
        let realProjModified = false;
        const cleanedRealTasks = (proj.tasks || []).map(t => {
          if (!Array.isArray(t.projectIds) || t.projectIds.length === 0) return t;
          const hasInvalidId = t.projectIds.some(pid => isSystemProject(pid));
          if (!hasInvalidId) return t;
          realProjModified = true;
          const validIds = t.projectIds
            .map(pid => resolveValidRealPid(pid))
            .filter((pid): pid is string => Boolean(pid));
          const nextIds = Array.from(new Set([proj.id, ...validIds]));
          const cleanedTask: Task = { ...t, projectIds: nextIds };
          changedTasks.push(cleanedTask);
          return cleanedTask;
        });
        if (realProjModified) {
          affectedSet.add(proj.id);
          return { ...proj, tasks: cleanedRealTasks };
        }
        return proj;
      }

      let projModified = false;
      const nextTasks: Task[] = [];

      (proj.tasks || []).forEach(t => {
        if (!isCommunicationItem(t, isComm)) {
          nextTasks.push(t);
          return;
        }

        const realIdList = collectMatchedRealProjectIds(t, proj.workspace);
        if (realIdList.length === 0) {
          nextTasks.push(t);
          return;
        }

        const currentIds = Array.isArray(t.projectIds) ? t.projectIds : [];
        const validCurrentIds = currentIds
          .map(pid => resolveValidRealPid(pid))
          .filter((pid): pid is string => Boolean(pid));
        const mergedProjectIds = Array.from(new Set([...validCurrentIds, ...realIdList]));

        if (isPersonRecord(t)) {
          const needsProjectIdFix = !isComm || !isCommProject(t.projectId || proj.id);
          const needsUpdate =
            needsProjectIdFix ||
            mergedProjectIds.length !== currentIds.length ||
            mergedProjectIds.some(pid => !currentIds.includes(pid));

          const healedRecord: Task = needsUpdate
            ? {
                ...t,
                projectId: 'comm_internal',
                projectIds: mergedProjectIds,
              }
            : t;

          if (!isComm) {
            // Rescue person record out of inbox_inforum back into comm_internal
            projModified = true;
            affectedSet.add(proj.id);
            affectedSet.add('comm_internal');
            changedTasks.push(healedRecord);
            personRecordsToMoveToComm.push(healedRecord);
          } else if (needsUpdate) {
            projModified = true;
            affectedSet.add(proj.id);
            changedTasks.push(healedRecord);
            nextTasks.push(healedRecord);
          } else {
            nextTasks.push(t);
          }
        } else {
          const primaryTargetProjId = realIdList[0];
          const targetProjExists = realProjects.some(rp => areProjectIdsMatching(rp.id, primaryTargetProjId));
          if (targetProjExists) {
            projModified = true;
            affectedSet.add(proj.id);
            affectedSet.add(primaryTargetProjId);
            const healedVorgang: Task = {
              ...t,
              projectId: primaryTargetProjId,
              projectIds: mergedProjectIds,
              category: t.category || 'Kommunikation',
            };
            changedTasks.push(healedVorgang);
            const list = tasksToMoveToProject.get(primaryTargetProjId) || [];
            list.push(healedVorgang);
            tasksToMoveToProject.set(primaryTargetProjId, list);
          } else {
            nextTasks.push(t);
          }
        }
      });

      return projModified ? { ...proj, tasks: nextTasks } : proj;
    })
    .map(proj => {
      if (isCommProject(proj.id)) {
        if (personRecordsToMoveToComm.length === 0) return proj;
        const existingIds = new Set((proj.tasks || []).map(t => String(t.id)));
        const newRecords = personRecordsToMoveToComm.filter(t => !existingIds.has(String(t.id)));
        if (newRecords.length === 0) return proj;
        affectedSet.add(proj.id);
        return {
          ...proj,
          tasks: [...(proj.tasks || []), ...newRecords],
        };
      }
      if (isSystemProject(proj.id, proj.name)) return proj;
      let incoming: Task[] = [];
      tasksToMoveToProject.forEach((list, targetPid) => {
        if (areProjectIdsMatching(proj.id, targetPid)) {
          incoming = incoming.concat(list);
        }
      });
      if (incoming.length === 0) return proj;
      const existingIds = new Set((proj.tasks || []).map(t => String(t.id)));
      const newOnes = incoming.filter(t => !existingIds.has(String(t.id)));
      if (newOnes.length === 0) return proj;
      affectedSet.add(proj.id);
      return {
        ...proj,
        tasks: [...(proj.tasks || []), ...newOnes],
      };
    });

  return {
    updatedProjects,
    changedTasks,
    affectedProjectIds: Array.from(affectedSet),
  };
}

